// Helpers used across the Actions Toolkit components. Mirrors the design's
// utils.jsx so all naming/conventions stay in lockstep with the prototype.

import Tools from '@decisiv/iconix/lib/components/Tools';
import Plug from '@decisiv/iconix/lib/components/Plug';
import Clipboard from '@decisiv/iconix/lib/components/Clipboard';
import Building from '@decisiv/iconix/lib/components/Building';
import Antenna from '@decisiv/iconix/lib/components/Antenna';
import Shield from '@decisiv/iconix/lib/components/Shield';
import Pin from '@decisiv/iconix/lib/components/Pin';
import Folder from '@decisiv/iconix/lib/components/Folder';
import Network from '@decisiv/iconix/lib/components/Network';

// 18% of the category hue over whatever surface is behind it, so the chip
// tint follows the theme without a second palette.
const tint = (hex) => `${hex}2E`;

// Static catalog of category UI metadata. Backend-authored category UUIDs are
// resolved by name against this list — the API/serializer returns `attributes.name`,
// and we look that up to pick the right color/icon. Names match the constant
// in app/layers/common/ruby/lib/constants/categories_and_actions_value.rb.
//
// `icon` is an iconix component reference; render via
//   const Icon = meta.icon; <Icon size="small" />
export const CATEGORY_META = [
  { name: 'Operations', color: '#E89146', bg: tint('#E89146'), icon: Tools },
  { name: 'PlatformApi', color: '#3F7BC9', bg: tint('#3F7BC9'), icon: Plug },
  {
    name: 'Service Management',
    color: '#9CB23A',
    bg: tint('#9CB23A'),
    icon: Clipboard,
  },
  {
    name: 'Service Provider Business System',
    color: '#D86B3A',
    bg: tint('#D86B3A'),
    icon: Building,
  },
  { name: 'Telematics', color: '#7C49B8', bg: tint('#7C49B8'), icon: Antenna },
  {
    name: 'Warranty and Recall',
    color: '#3D6BB8',
    bg: tint('#3D6BB8'),
    icon: Shield,
  },
  {
    name: 'Asset Management',
    color: '#C7474B',
    bg: tint('#C7474B'),
    icon: Pin,
  },
  { name: 'Case', color: '#6E26A6', bg: tint('#6E26A6'), icon: Folder },
  {
    name: 'Dealer Management System',
    color: '#2E6E76',
    bg: tint('#2E6E76'),
    icon: Network,
  },
  {
    name: 'Uncategorized',
    color: 'var(--if-ink-2)',
    bg: 'var(--if-surface-3)',
    icon: Folder,
  },
];

const FALLBACK_META = {
  name: '—',
  color: 'var(--if-ink-2)',
  bg: 'var(--if-surface-3)',
  icon: Folder,
};

export function getCategoryMeta(name) {
  return CATEGORY_META.find((c) => c.name === name) || FALLBACK_META;
}

export function fmtDate(s) {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Verb-keyed name validation, identical regex to backend Action#name format.
export function validateActionName(name, existingNames) {
  if (!name || !name.trim()) return 'Name is required';
  if (!/^[a-z][a-z0-9_]*$/.test(name)) {
    return 'Use lowercase letters, numbers, and underscores. Must start with a letter.';
  }
  if (!/^[a-z]+_/.test(name)) {
    return 'Name must be verb-keyed (e.g. retrieve_parts, add_metadata_to_case)';
  }
  if (existingNames && existingNames.includes(name)) {
    return 'An action with this name already exists';
  }
  return null;
}

// Parse JSON, returning either { ok: true, data } or { ok: false, err, line?, col? }.
export function safeParse(txt) {
  if (!txt || !txt.trim()) return { ok: false, err: 'Empty schema' };
  try {
    const data = JSON.parse(txt);
    return { ok: true, data };
  } catch (e) {
    const m = /position\s+(\d+)/.exec(e.message);
    let line = null;
    let col = null;
    if (m) {
      const pos = parseInt(m[1], 10);
      const before = txt.slice(0, pos);
      line = (before.match(/\n/g) || []).length + 1;
      col = pos - before.lastIndexOf('\n');
    }
    return { ok: false, err: e.message, line, col };
  }
}

// Turns a JSON-Schema pointer in the server message into a friendly dotted
// path ("#/properties/raw_payload/type" → "raw_payload.type"); null if none.
function extractSchemaPath(detail) {
  const m = /#\/([^'\s]+)/.exec(detail);
  if (!m) return null;
  const segments = m[1]
    .split('/')
    .filter((s) => s && s !== 'properties' && s !== 'items');
  return segments.length ? segments.join('.') : '(root)';
}

// Best-effort parser for the server's JSON-Schema validation errors into a
// shape the drawer can render; `raw` keeps the untouched server detail.
export function parseServerError(detail) {
  if (!detail) return null;
  const looksLikeSchema = /#\/|did not match/.test(detail);
  if (!looksLikeSchema) {
    return { title: 'Save failed', summary: detail };
  }

  const field = extractSchemaPath(detail);
  const typeAnyPattern =
    /value\s+"any"\s+did not match one of the following values/i;

  // `"type": "any"` — Draft 3 carryover. Common pitfall worth a friendly hint.
  if (typeAnyPattern.test(detail)) {
    return {
      title: 'Invalid JSON Schema',
      field,
      summary: '"any" isn’t a valid type in JSON Schema Draft 7.',
      hint: 'Omit the "type" keyword to allow any value, use {} for an empty schema, or list each type explicitly — e.g. ["string", "null"].',
      raw: detail,
    };
  }

  // Generic schema error — extract a one-line summary plus keep the raw.
  const firstLine = detail.split('\n')[0].trim().replace(/\.$/, '');
  return {
    title: 'Invalid JSON Schema',
    field,
    summary: firstLine,
    raw: detail,
  };
}

// Lenient Draft-7 meta check — does the object look like a schema?
export function checkDraft7(obj) {
  if (!obj || typeof obj !== 'object') return 'Schema must be an object';
  if (!obj.type && !obj.properties && !obj.$ref && !obj.oneOf && !obj.anyOf) {
    return 'Schema must declare a type or properties';
  }
  return null;
}

export function prettyJSON(val) {
  try {
    return JSON.stringify(val, null, 2);
  } catch (e) {
    return String(val);
  }
}

// Returns an HTML string with JSON tokens wrapped in span.json-{key,str,num,bool,null}.
// Consumer renders this via dangerouslySetInnerHTML inside the schema viewer.
export function renderHighlightedJSON(val) {
  const str = prettyJSON(val);
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(
      /("(?:\\u[a-fA-F0-9]{4}|\\[^u]|[^\\"])*"(?:\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
      (m) => {
        let cls = 'json-num';
        if (/^"/.test(m)) cls = /:$/.test(m) ? 'json-key' : 'json-str';
        else if (/true|false/.test(m)) cls = 'json-bool';
        else if (/null/.test(m)) cls = 'json-null';
        return `<span class="${cls}">${m}</span>`;
      },
    );
}

// ---- API → UI shape conversion -------------------------------------------
//
// The backend returns a JSONAPI Action with chain pointers (supersedes_uuid,
// successor_uuid). The page expects each action grouped with its full version
// chain inline so the version pill segmented control + Versions tab can render
// without N round-trips. `assembleChain` walks chains client-side by name —
// all rows sharing a chain root get folded into a single action shape:
//
//   { id, name, alias, description, category, versions: [{ version, isCurrent,
//     locked, integrations, inputSchema, outputSchema, metadataSchema,
//     createdAt, createdBy, uuid, supersedes_uuid }] }
//
// `version` numbers are 1-indexed by traversal order. Names of successive
// versions don't have to match — the chain is the source of truth.

function toActionShape(rootRow, versions) {
  const attrs = rootRow.attributes;
  return {
    id: rootRow.id,
    name: attrs.name,
    alias: attrs.alias || '',
    description: attrs.description || '',
    category: attrs.category_name || attrs.category || 'Uncategorized',
    versions,
  };
}

function toVersionShape(row, versionNumber, overrides = {}) {
  const attrs = row.attributes;
  return {
    uuid: row.id,
    version: versionNumber,
    createdAt: attrs.created_at,
    createdBy: attrs.created_by || 'system',
    supersedes_uuid: attrs.supersedes_uuid || null,
    isCurrent: false,
    locked: Boolean(attrs.locked),
    integrations: [], // populated lazily via fetchActionIntegrations
    inputSchema: attrs.input_schema || {},
    outputSchema: attrs.output_schema || {},
    metadataSchema: attrs.metadata_schema || {},
    // Read alias / description / category off the row itself so the drawer
    // shows the selected version's values, not the (often blank) chain root's.
    alias: attrs.alias || '',
    description: attrs.description || '',
    category: attrs.category_name || attrs.category || 'Uncategorized',
    changeNote: attrs.change_note || null,
    ...overrides,
  };
}

export function assembleChains(actionRows) {
  if (!actionRows || actionRows.length === 0) return [];

  const byUuid = new Map();
  actionRows.forEach((row) => byUuid.set(row.id, row));

  // Find every root (no supersedes_uuid) and walk forward.
  const roots = actionRows.filter((row) => !row.attributes.supersedes_uuid);
  const seen = new Set();
  const chains = [];

  roots.forEach((root) => {
    const chain = [];
    let cursor = root;
    let version = 1;
    while (cursor && !seen.has(cursor.id)) {
      chain.push(toVersionShape(cursor, version));
      seen.add(cursor.id);
      const next = cursor.attributes.successor_uuid
        ? byUuid.get(cursor.attributes.successor_uuid)
        : null;
      cursor = next;
      version += 1;
    }
    if (chain.length > 0) chain[chain.length - 1].isCurrent = true;
    chains.push(toActionShape(root, chain));
  });

  // Pick up orphans (chain root not in result set — typical for paginated lists).
  actionRows.forEach((row) => {
    if (seen.has(row.id)) return;
    seen.add(row.id);
    chains.push(
      toActionShape(row, [toVersionShape(row, 1, { isCurrent: true })]),
    );
  });

  return chains;
}

// Look up "current" version inside an action shape: the row marked isCurrent,
// or the highest-numbered row as fallback.
export function currentVersion(action) {
  if (!action) return null;
  return (
    action.versions.find((v) => v.isCurrent) ||
    action.versions[action.versions.length - 1]
  );
}
